import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { useSelector } from "react-redux";
import useHandleBlur from "../../../functions/useHandleBlur";
import { useFormState } from "../../../functions/useFormState"; // Reusable form hook
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import { selectOwnersManual, selectFieldDescriptions } from "../../../redux/selectors";
import { selectApiStatus } from "../../../redux/slices/apiStatusSlice"; // Import the selector
import useFieldClass from "../../../functions/useFieldClass";
import NoRecordFoundAlert from "../../common/NoRecordFoundAlert";

function OwnersManual(zone) {
  const userDetails = useSelector(selectUserDetails); // User details
  const userRoles = useSelector(selectUserRoles); // User details
  const currentUser = userDetails?.name;
  const sectionName = "ownersManual";

  const apiUrls = useSelector(selectApiUrls); // Fetch API URLs from Redux
  const apiUrl = apiUrls[sectionName];
  const tableKey = sectionName;

  const error = null;

  const storedJobDetails = useSelector(selectOwnersManual);

  const apiStatus = useSelector(selectApiStatus); // Fetch API status from Redux
  const loading = apiStatus[sectionName] !== "success"; // Determine loading state based on API status

  const jobDetails = useMemo(() => {
    return storedJobDetails[0] || {};
  }, [storedJobDetails]);

  const { formData, handleChange, setFormData } = useFormState({}); // Form state management

  useEffect(() => {
    if (jobDetails) {
      setFormData({
        ID: jobDetails.ID || "",
        mediaType: jobDetails.MediaType || "",
        quantity: jobDetails.Quantity || "",
      });
    }
  }, [jobDetails, setFormData]);

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly; // Update isDisabled condition

  // State to store role variables
  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
    Engineer: false,
    TestPit: false,
    PSN: false,
    Sales: false,
    ReadOnly: false,
  });

  const { handleFocus, handleBlur, showNotification, saveStatus } =
    useHandleBlur(formData);

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  const fieldClasses = useFieldClass(formData, zone); // Use useFieldClass hook

  const fieldDescriptions = useSelector(selectFieldDescriptions);

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ); // Show loading spinner
  }

  if (error) {
    return <div className="error">{error}</div>; // Show error message if fetch fails
  }

  if (!loading && !jobDetails?.ID) {
    return (
      <NoRecordFoundAlert sectionName={sectionName} zone={zone} />
    );
  }

  return (
    <div>
      {error ? (
        <p style={{ color: "red" }}>Error: {error}</p>
      ) : !formData ? (
        <p>Loading...</p>
      ) : (
        <div>
          <div>
            {showNotification && (
              <div
                className={`save-notification ${
                  saveStatus.includes("Failed") ? "error" : ""
                }`}
              >
                {saveStatus}
              </div>
            )}
          </div>
          <div>
            <Row>
              <Col>
                <Form.Group as={Row} controlId="mediaType">
                  <Form.Label column="md" sm={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("MediaType")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Media Type</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col sm={9}>
                    <Form.Control
                      type="text"
                      name="mediaType"
                      value={formData.mediaType}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Media Type", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "MediaType",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.mediaType}
                      onChange={handleChange}
                      placeholder="Enter Media Type"
                    />
                  </Col>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group as={Row} controlId="quantity">
                  <Form.Label column="md" sm={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("Quantity")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Quantity</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col sm={9}>
                    <Form.Control
                      type="text"
                      name="quantity"
                      value={formData.quantity}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Quantity", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "Quantity",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.quantity}
                      onChange={handleChange}
                      placeholder="Enter Quantity"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
          </div>
        </div>
      )}
    </div>
  );
}

export default OwnersManual;
